/**
 * Serviço para verificação do Pacote Cenoura
 */

import { apiService } from './api.service';
import { CarrotInfo } from '../models/carrot.models';
import { environment } from '../config/environment';
import { logger } from '../utils/logger.util';

export class CarrotService {
  /**
   * Verifica se o dispositivo está conectado à internet
   * @returns Promise<boolean> - true se conectado, false caso contrário
   */
  async checkInternetConnection(): Promise<boolean> {
    // Verificar primeiro se há conexão de rede disponível (navigator.onLine)
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      logger.debug('[CarrotService] navigator.onLine indica que está offline');
      return false;
    }

    try {
      // Tentar fazer uma requisição para verificar conectividade
      // Usar o endpoint da Akross para verificar acesso à internet
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000); // Timeout de 5 segundos

      const response = await fetch('https://access-network.akross.com.br/', {
        method: 'GET',
        mode: 'cors',
        signal: controller.signal,
        cache: 'no-cache',
        headers: {
          'Accept': '*/*',
          'Accept-Language': 'pt-BR,pt;q=0.9,en-US;q=0.8,en;q=0.7',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Origin': window.location.origin,
          'Referer': window.location.href
        }
      });

      clearTimeout(timeoutId);

      // Se a resposta foi bem-sucedida, está conectado
      return response.ok;
    } catch (error) {
      logger.debug('[CarrotService] Sem conexão com internet');
      return false;
    }
  }

  /**
   * Busca informações do pacote cenoura da API
   * @returns Promise<CarrotInfo | null> - Informações do pacote ou null em caso de erro
   */
  async getCarrotInfo(): Promise<CarrotInfo | null> {
    try {
      const url = `${environment.apiBaseUrl}carrot/info`;

      const response = await apiService.get<{ active: boolean; countdown: number | null }>(url, {
        headers: {
          'X-CONNECTIVITY': 'false'
        }
      });
      return CarrotInfo.fromJson(response);
    } catch (error) {
      logger.error('[CarrotService] Erro ao buscar informações do pacote cenoura:', error);
      return null;
    }
  }

  /**
   * Verifica o pacote cenoura e retorna as informações se necessário
   * Esta função só faz a chamada à API se o usuário estiver sem conexão com internet
   * (mas ainda pode ter dados móveis para acessar a API)
   * @returns Promise<CarrotInfo | null> - Informações do pacote ou null se não deve exibir countdown
   */
  async checkCarrotPackage(): Promise<CarrotInfo | null> {
    // Verificar conectividade primeiro
    // TODO: Remover mock - descomentar linha abaixo e remover linha hardcoded
    const isConnected = await this.checkInternetConnection();
    // const isConnected = false; // MOCK: sempre retorna false para forçar verificação do pacote cenoura

    if (isConnected) {
      // Se está conectado à internet, não precisa verificar o pacote cenoura
      logger.debug('[CarrotService] Usuário está conectado à internet, não verificando pacote cenoura');
      return null;
    }

    // Se não está conectado à internet (mas pode ter dados móveis), buscar informações do pacote cenoura
    logger.debug('[CarrotService] Usuário sem conexão com internet, verificando pacote cenoura...');
    // TODO: Remover mock - descomentar linha abaixo e remover linha hardcoded
    return await this.getCarrotInfo();
    // return new CarrotInfo(true, 10); // MOCK: sempre retorna pacote ativo com countdown de 10 segundos
  }
}

export const carrotService = new CarrotService();
